import { formatDatetime, useTranslate } from '@si/utils';
import { useCallback, useMemo } from 'react';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

import type { Translation } from '../utils/internationalisation/useTranslate';
import { notUndefined, validNrOfDecimalPoints } from './utils';

type SchemaOptions = {
  label?: Translation;
  optional?: boolean;
};

type DateSchemaOptions = SchemaOptions & {
  earliest?: Date;
  latest?: Date;
};

export const useZod = () => {
  const { t, translate } = useTranslate();

  const fieldRequiredError = useCallback(
    (translation: Translation) => ({
      message: translate(t.fieldRequired, { field: translate(translation) }),
    }),
    [t.fieldRequired, translate]
  );

  const createStringSchema = useCallback(
    (label: Translation | string, min: number, max: number) => {
      const field = typeof label === 'string' ? label : translate(label);

      let stringSchema = z.string({
        error: (issue) => (!issue.input ? translate(t.fieldRequired, { field }) : undefined),
      });

      if (max === min) {
        stringSchema = stringSchema
          .min(1, { message: translate(t.fieldRequired, { field }) })
          .length(max, {
            message: translate(t.exactCharacters, { field, exact: max }),
          });
      } else {
        stringSchema = stringSchema.max(max, {
          message: translate(t.maxCharacters, { field, max }),
        });
      }

      if (max !== min && min > 0) {
        stringSchema = stringSchema.min(min, {
          message:
            min === 1
              ? translate(t.fieldRequired, { field })
              : translate(t.minCharacters, { field, min }),
        });
      }

      return stringSchema;
    },

    [t, translate]
  );

  const createPhoneNrSchema = useCallback(
    ({ label = t.phoneNr, optional = false }: SchemaOptions = {}) => {
      return z
        .string({
          error: (issue) =>
            !issue.input ? translate(t.fieldRequired, { field: translate(label) }) : undefined,
        })
        .refine((value) => optional || !!value, {
          message: translate(t.fieldRequired, { field: translate(label) }),
        })
        .refine((value) => (optional && !value ? true : isPossiblePhoneNumber(value ?? '', 'NO')), {
          message: translate(t.fieldInvalid, { field: translate(label) }),
        });
    },
    [t, translate]
  );

  const createIntSchema = useCallback(
    (label: Translation | string, min: number, max: number) => {
      const field = typeof label === 'string' ? label : translate(label);

      return z
        .number({
          error: (issue) => (!issue.input ? translate(t.fieldRequired, { field }) : undefined),
        })
        .int({ message: translate(t.mustBeInteger, { field }) })
        .min(min, translate(t.minNumber, { field, min }))
        .max(max, translate(t.maxNumber, { field, max }))
        .optional()
        .refine(notUndefined, {
          message: translate(t.fieldRequired, { field }),
        });
    },
    [t, translate]
  );

  const createFloatSchema = useCallback(
    (label: Translation | string, min: number, max: number, decimalPoints = 2) => {
      const field = typeof label === 'string' ? label : translate(label);

      return z
        .number({
          error: (issue) => (!issue.input ? translate(t.fieldRequired, { field }) : undefined),
        })
        .min(min, translate(t.minNumber, { field, min }))
        .max(max, translate(t.maxNumber, { field, max }))
        .optional()
        .refine(notUndefined, {
          message: translate(t.fieldRequired, { field }),
        })
        .refine((value?: number) => validNrOfDecimalPoints(value, decimalPoints), {
          message: translate(t.maxDecimalPoints, {
            field,
            max: decimalPoints,
          }),
        });
    },
    [t, translate]
  );

  const createEmailSchema = useCallback(
    ({ label = t.email, optional = false }: SchemaOptions = {}) => {
      if (optional) {
        return z.union([
          z.literal(''),
          z
            .email({
              error: (issue) =>
                translate(!issue.input ? t.fieldRequired : t.fieldInvalid, {
                  field: translate(label),
                }),
            })
            .optional(),
        ]);
      }
      return z.email({
        error: (issue) =>
          translate(!issue.input ? t.fieldRequired : t.fieldInvalid, {
            field: translate(label),
          }),
      });
    },
    [t, translate]
  );

  const createDateSchema = useCallback(
    ({ label = t.date, earliest, latest }: DateSchemaOptions = {}) => {
      if (earliest) {
        earliest.setHours(0, 0, 0, 0);
      }
      if (latest) {
        latest.setHours(23, 59, 59, 999);
      }

      return z
        .date({
          error: (issue) =>
            !issue.input ? translate(t.fieldRequired, { field: translate(label) }) : undefined,
        })
        .min(earliest ?? new Date(0), {
          message: translate(t.fieldMustBeAfter, {
            field: translate(label),
            after: formatDatetime(earliest ?? new Date(0), 'dateWithYearShort'),
          }),
        })
        .max(latest ?? new Date(Number.MAX_SAFE_INTEGER), {
          message: translate(t.fieldMustBeBefore, {
            field: translate(label),
            before: formatDatetime(
              latest ?? new Date(Number.MAX_SAFE_INTEGER),
              'dateWithYearShort'
            ),
          }),
        });
    },
    [t, translate]
  );

  const zodSchema = useMemo(
    () => ({
      firstName: createStringSchema(t.firstName, 1, 50),
      lastName: createStringSchema(t.lastName, 1, 50),
      email: createEmailSchema(),
      phoneNr: createPhoneNrSchema(),
    }),
    [createPhoneNrSchema, createStringSchema, createEmailSchema, t]
  );

  return {
    createDateSchema,
    createEmailSchema,
    createFloatSchema,
    createIntSchema,
    createStringSchema,
    createPhoneNrSchema,
    fieldRequiredError,
    zodSchema,
  };
};
